import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ZiroModalService } from '../../shared/services/ziro-modal.service';
import { Observable, Subscriber } from 'rxjs';
import { DirectoryNumber, DirectoryNumberRef } from '../../shared/models/generated/ziroModels';
import { AuditHeaderService } from '../../shared/services/audit-header.service';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../button/button.component';
import { Nvp } from '../../shared/models/nvp';
import { HttpOptions } from '../../shared/resources/abstract/model.resource.abstract';

@Injectable()
export class DirectoryNumberResource {
  private http = inject(HttpClient);
  private ziroModalService = inject(ZiroModalService);
  private auditHeaderService = inject(AuditHeaderService);

  private _buildHttpOptions(auditTags: Nvp[]): HttpOptions | object {
    return auditTags ? { headers: this.auditHeaderService.buildHeader(auditTags) } : {};
  }

  post(directoryNumber: DirectoryNumber, serverId: string, auditTags?: Nvp[]): Observable<DirectoryNumberRef> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return new Observable<DirectoryNumberRef>((subscriber: Subscriber<DirectoryNumberRef>) => {
      this.http
        .post(
          `/services/cisco/macs/cucm-servers/${encodeURIComponent(serverId)}/directory-numbers`,
          directoryNumber,
          httpOptions,
        )
        .subscribe({
          next: (ref: DirectoryNumberRef) => {
            subscriber.next(ref);
            subscriber.complete();
          },
          error: (error) => {
            if (error.status === 422 && error.error.reasonCode === 'FORBIDDEN_DN_MODIFICATION') {
              const options = {
                modalViewProperties: {
                  title: 'Forbidden',
                  promptBody: error.error.description,
                  icon: ZiroIcons.FORBIDDEN,
                  iconClass: 'text-danger',
                  displayCloseButton: true,
                  buttons: [
                    {
                      label: 'OK',
                      buttonClass: ButtonStyles.OUTLINE_INFO,
                      dataAutomation: 'forbidden-dismiss-button',
                    },
                  ],
                },
              };
              this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
              subscriber.next(error);
            } else {
              subscriber.error(error);
            }
            subscriber.complete();
          },
        });
    });
  }

  get(id: string, serverId: string): Observable<DirectoryNumber> {
    return this.http.get<DirectoryNumber>(
      `/services/cisco/macs/cucm-servers/${encodeURIComponent(serverId)}/directory-numbers/${encodeURIComponent(id)}`,
    );
  }

  put(id: string, directoryNumber: DirectoryNumber, serverId: string, auditTags?: any): Observable<DirectoryNumberRef> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return new Observable<DirectoryNumberRef>((subscriber: Subscriber<DirectoryNumberRef>) => {
      this.http
        .put(
          `/services/cisco/macs/cucm-servers/${encodeURIComponent(serverId)}/directory-numbers/${encodeURIComponent(
            id,
          )}`,
          directoryNumber,
          httpOptions,
        )
        .subscribe({
          next: (ref: DirectoryNumberRef) => {
            subscriber.next(ref);
            subscriber.complete();
          },
          error: (error) => {
            if (error.status === 422 && error.error.reasonCode === 'FORBIDDEN_DN_MODIFICATION') {
              const options = {
                modalViewProperties: {
                  title: 'tkey;forbidden.user.title',
                  promptBody: error.error.description,
                  icon: ZiroIcons.FORBIDDEN,
                  iconClass: 'text-danger',
                  displayCloseButton: true,
                  buttons: [
                    {
                      label: 'tkey;dialogs.button.ok',
                      buttonClass: ButtonStyles.OUTLINE_INFO,
                      dataAutomation: 'forbidden-dismiss-button',
                    },
                  ],
                },
              };
              this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
            }

            subscriber.error(error);
            subscriber.complete();
          },
        });
    });
  }

  delete(id: string, serverId: number | string, auditTags?: Nvp[]): Observable<void> {
    const options = this.auditHeaderService.getHttpOptions(auditTags);
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.http
        .delete<void>(
          `/services/cisco/macs/cucm-servers/${encodeURIComponent(
            String(serverId),
          )}/directory-numbers/${encodeURIComponent(id)}`,
          options,
        )
        .subscribe({
          next: () => {
            subscriber.next();
            subscriber.complete();
          },
          error: (error) => {
            if (error.status === 422 && error.error.reasonCode === 'FORBIDDEN_DN_MODIFICATION') {
              const modalOptions = {
                modalViewProperties: {
                  title: 'Forbidden',
                  promptBody: error.error.description,
                  icon: ZiroIcons.FORBIDDEN,
                  iconClass: 'text-danger',
                  displayCloseButton: true,
                  buttons: [
                    {
                      label: 'OK',
                      buttonClass: ButtonStyles.OUTLINE_INFO,
                      dataAutomation: 'forbidden-dismiss-button',
                    },
                  ],
                },
              };
              this.ziroModalService
                .openPromptModal(() => modalOptions.modalViewProperties, modalOptions)
                .subscribe((response) => {
                  if (!response) {
                    subscriber.error(error);
                  }
                  subscriber.complete();
                });
            } else {
              // Display error toasts
              subscriber.error(error);
              subscriber.complete();
            }
          },
        });
    });
  }
}
